import { useState } from "react";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { useInvoicesStore } from "./invoicesStore";
import {
  Invoice,
  UpdateInvoiceParams,
  InvoiceStatusEnum,
  InvoiceLineInput,
  Permission,
} from "@forgepoint/shared";
import { Loader, Trash2 } from "lucide-react";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { toast } from "sonner";
import { InvoiceEditor } from "./InvoiceEditor";
import { formatCurrency } from "@/lib/utils";
import { useHasPermission } from "@/store/authStore";

export default function InvoicesController() {
  const canEdit = useHasPermission(Permission.INVOICES_EDIT);

  const {
    invoices,
    listLoading,
    listError,
    invoiceLoadingStates,
    fetchInvoices,
    createInvoice,
    updateInvoice,
    updateInvoiceStatus,
    deleteInvoice,
  } = useInvoicesStore();

  const {
    entitiesArray: invoicesArray,
    handleSearch,
    setDisplayedEntityIds: setDisplayedInvoiceIds,
  } = useEntitySearch({
    entities: invoices,
    fetchEntities: (params) => fetchInvoices(params?.search),
  });

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [formData, setFormData] = useState<{
    salesOrderId: string | undefined;
    customerCompanyId: string | undefined;
    lines: InvoiceLineInput[];
  }>({
    salesOrderId: undefined,
    customerCompanyId: undefined,
    lines: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const columns: DataTableColumn<Invoice>[] = [
    {
      key: "invoiceNumber",
      header: "Invoice #",
      type: "string",
      width: 120,
      format: (value: unknown) => `INV-${value}`,
    },
    {
      key: "total",
      header: "Total",
      type: "string",
      width: 120,
      align: "right",
      format: (value: unknown) =>
        value ? formatCurrency(value as string) : "-",
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      width: 100,
      format: (value: unknown) => {
        const status = value as string;
        return status.toLowerCase().replace("_", " ");
      },
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      width: 120,
    },
    {
      key: "actions",
      header: "",
      width: 100,
      format: (_value: unknown, invoice: Invoice) => {
        const loadingState = invoiceLoadingStates.get(invoice.id);
        const isDeleting = loadingState?.isLoadingDelete;

        return (
          <div className="flex gap-1">
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(invoice);
                }}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const handleCreate = () => {
    setFormData({
      salesOrderId: undefined,
      customerCompanyId: undefined,
      lines: [],
    });
    setIsCreateSheetOpen(true);
  };

  const handleEdit = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setFormData({
      salesOrderId: invoice.salesOrderId || undefined,
      customerCompanyId: invoice.customerCompanyId || undefined,
      lines: (invoice.lines || []).map((line) => ({
        itemId: line.itemId,
        description: line.description,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      })),
    });
    setIsEditSheetOpen(true);
  };

  const handleDelete = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveCreate = async () => {
    if (!formData.lines || formData.lines.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }

    setIsSaving(true);
    try {
      const result = await createInvoice({
        salesOrderId: formData.salesOrderId,
        customerCompanyId: formData.customerCompanyId,
        lines: formData.lines,
      });
      if (result) {
        setDisplayedInvoiceIds((prev) => [result.id, ...prev]);
        toast.success("Invoice created successfully");
        setIsCreateSheetOpen(false);
        setFormData({
          salesOrderId: undefined,
          customerCompanyId: undefined,
          lines: [],
        });
      } else {
        toast.error("Failed to create invoice");
      }
    } catch (_error) {
      toast.error("Failed to create invoice");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedInvoice) {
      return;
    }

    if (!formData.lines || formData.lines.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }

    setIsSaving(true);
    try {
      const updateData: UpdateInvoiceParams = {};

      if (formData.salesOrderId !== selectedInvoice.salesOrderId) {
        updateData.salesOrderId = formData.salesOrderId;
      }

      if (formData.customerCompanyId !== selectedInvoice.customerCompanyId) {
        updateData.customerCompanyId = formData.customerCompanyId;
      }

      // Always update lines to ensure changes are saved
      updateData.lines = formData.lines;

      const result = await updateInvoice(selectedInvoice.id, updateData);
      if (result) {
        toast.success("Invoice updated successfully");
        setIsEditSheetOpen(false);
        setSelectedInvoice(null);
        setFormData({
          salesOrderId: undefined,
          customerCompanyId: undefined,
          lines: [],
        });
      } else {
        toast.error("Failed to update invoice");
      }
    } catch (_error) {
      toast.error("Failed to update invoice");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!selectedInvoice) {
      return;
    }

    setIsFinalizing(true);
    try {
      const result = await updateInvoiceStatus(
        selectedInvoice.id,
        InvoiceStatusEnum.FINALIZED,
      );
      if (result) {
        toast.success("Invoice finalized successfully");
        setSelectedInvoice(result);
      } else {
        toast.error("Failed to finalize invoice");
      }
    } catch (_error) {
      toast.error("Failed to finalize invoice");
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedInvoice) {
      return;
    }

    const success = await deleteInvoice(selectedInvoice.id);
    if (success) {
      setDisplayedInvoiceIds((prev) =>
        prev.filter((id) => id !== selectedInvoice.id),
      );
      toast.success("Invoice deleted successfully");
    } else {
      toast.error("Failed to delete invoice");
    }

    setIsDeleteDialogOpen(false);
    setSelectedInvoice(null);
  };

  if (listError) {
    return (
      <div className="p-8 text-red-600">
        Error loading invoices: {listError}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DataTable
        columns={columns}
        data={invoicesArray}
        onClickRow={(invoice) => handleEdit(invoice)}
        onChangeSearch={handleSearch}
        searchPlaceholder="Search invoices..."
        className="h-full"
        isLoading={listLoading && invoicesArray.length === 0}
        onCreate={canEdit ? handleCreate : undefined}
        createButtonText="New Invoice"
      />

      {/* Create Invoice Sheet */}
      <Sheet open={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen}>
        <SheetContent className="w-full sm:max-w-[900px] flex flex-col p-0">
          <div className="px-6 py-4 border-b">
            <SheetHeader>
              <SheetTitle>Create New Invoice</SheetTitle>
              <SheetDescription>
                Create a new invoice. The invoice number will be assigned
                automatically.
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-hidden">
            <InvoiceEditor
              invoice={null}
              showHeader={false}
              onChange={(data) =>
                setFormData({
                  salesOrderId: data.salesOrderId,
                  customerCompanyId: data.customerCompanyId,
                  lines: data.lines,
                })
              }
            />
          </div>

          <div className="px-6 py-4 border-t">
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline" disabled={isSaving}>
                  Cancel
                </Button>
              </SheetClose>
              <Button onClick={handleSaveCreate} disabled={isSaving}>
                {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                Create Invoice
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Invoice Sheet */}
      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent className="w-full sm:max-w-[900px] flex flex-col p-0">
          <div className="px-6 py-4 border-b">
            <SheetHeader>
              <div className="flex items-center justify-between">
                <SheetTitle>
                  Edit Invoice{" "}
                  {selectedInvoice && `INV-${selectedInvoice.invoiceNumber}`}
                </SheetTitle>
                {canEdit &&
                  selectedInvoice?.status === InvoiceStatusEnum.DRAFT && (
                    <Button
                      onClick={handleFinalize}
                      disabled={isFinalizing}
                      variant="outline"
                      size="sm"
                    >
                      {isFinalizing && (
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Finalize Invoice
                    </Button>
                  )}
              </div>
              <SheetDescription>
                {selectedInvoice?.status === InvoiceStatusEnum.FINALIZED
                  ? "This invoice has been finalized and is read-only."
                  : "Update invoice information. The invoice number cannot be changed."}
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-hidden">
            <InvoiceEditor
              invoice={selectedInvoice}
              showHeader={false}
              readOnly={
                !canEdit ||
                selectedInvoice?.status === InvoiceStatusEnum.FINALIZED
              }
              onChange={(data) =>
                setFormData({
                  salesOrderId: data.salesOrderId,
                  customerCompanyId: data.customerCompanyId,
                  lines: data.lines,
                })
              }
            />
          </div>

          <div className="px-6 py-4 border-t">
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline" disabled={isSaving}>
                  Cancel
                </Button>
              </SheetClose>
              {canEdit &&
                selectedInvoice?.status !== InvoiceStatusEnum.FINALIZED && (
                  <Button onClick={handleSaveEdit} disabled={isSaving}>
                    {isSaving && (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Changes
                  </Button>
                )}
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        entityType="Invoice"
        itemName={
          selectedInvoice ? `INV-${selectedInvoice.invoiceNumber}` : undefined
        }
      />
    </div>
  );
}
